import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { MoneyInput } from '../components/MoneyInput';
import { format } from 'date-fns';
import { CalendarIcon, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const IntField = ({ label, name, value, onChange, required, helper }) => (
  <div className="space-y-1">
    <Label className="text-slate-700 text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input
      type="number"
      min="0"
      value={value}
      onChange={e => onChange(name, parseInt(e.target.value) || 0)}
      className="bg-white border-slate-200"
      data-testid={`eod-field-${name}`}
    />
    {helper && <p className="text-xs text-slate-400">{helper}</p>}
  </div>
);

export default function EODFormPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [duplicateId, setDuplicateId] = useState(null); // existing record ID if duplicate detected
  const [form, setForm] = useState({
    submission_date: format(new Date(), 'yyyy-MM-dd'),
    location_id: '',
    submitted_by: '',
    inbound_calls: '', calls_answered: '', appointments_scheduled: '',
    total_consults_scheduled: '', total_consults_showed: '',
    full_arch_cases_presented: '', non_full_arch_cases_presented: '',
    total_qualified_consults: '',
    closed_today_from_today: '', full_arch_sold_today_from_today: '',
    arches_sold_today_from_today: '', total_treatments_sold_today_from_today: '',
    closed_from_previous: '', full_arch_sold_from_previous: '',
    arches_sold_from_previous: '', non_full_arch_from_previous: '',
    total_treatments_sold_from_previous: '',
    total_collections_today: '', surgical_production_today: '',
    notes: ''
  });

  useEffect(() => {
    axios.get(`${API}/locations`).then(r => setLocations(r.data.filter(l => l.active))).catch(() => {});
    axios.get(`${API}/dropdowns`).then(r => setDropdowns(r.data)).catch(() => {});
  }, []);

  const setField = (name, val) => setForm(prev => ({ ...prev, [name]: val }));

  const validate = () => {
    const errs = [];
    if (!form.location_id) errs.push('Location is required');
    if (form.total_consults_showed > form.total_consults_scheduled)
      errs.push('Consults showed cannot exceed consults scheduled');
    if (form.full_arch_sold_today_from_today > form.full_arch_cases_presented)
      errs.push('Full arch sold (same day) cannot exceed full arch presented');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setLoading(true);
    try {
      await axios.post(`${API}/eod`, {
        ...form,
        inbound_calls: parseInt(form.inbound_calls) || 0,
        calls_answered: parseInt(form.calls_answered) || 0,
        appointments_scheduled: parseInt(form.appointments_scheduled) || 0,
        total_consults_scheduled: parseInt(form.total_consults_scheduled) || 0,
        total_consults_showed: parseInt(form.total_consults_showed) || 0,
        full_arch_cases_presented: parseInt(form.full_arch_cases_presented) || 0,
        non_full_arch_cases_presented: parseInt(form.non_full_arch_cases_presented) || 0,
        total_qualified_consults: form.total_qualified_consults !== '' ? parseInt(form.total_qualified_consults) || 0 : null,
        inbound_calls: 0, calls_answered: 0, appointments_scheduled: 0,
        form_type: 'sales',
        closed_today_from_today: parseInt(form.closed_today_from_today) || 0,
        full_arch_sold_today_from_today: parseInt(form.full_arch_sold_today_from_today) || 0,
        arches_sold_today_from_today: parseInt(form.arches_sold_today_from_today) || 0,
        total_treatments_sold_today_from_today: parseInt(form.total_treatments_sold_today_from_today) || 0,
        closed_from_previous: parseInt(form.closed_from_previous) || 0,
        full_arch_sold_from_previous: parseInt(form.full_arch_sold_from_previous) || 0,
        arches_sold_from_previous: parseInt(form.arches_sold_from_previous) || 0,
        non_full_arch_from_previous: parseInt(form.non_full_arch_from_previous) || 0,
        total_treatments_sold_from_previous: parseInt(form.total_treatments_sold_from_previous) || 0,
        total_collections_today: parseFloat(form.total_collections_today) || 0,
        surgical_production_today: parseFloat(form.surgical_production_today) || 0
      });
      toast.success('EOD record submitted successfully!');
      navigate('/eod/records');
    } catch (err) {
      // 409 = duplicate — offer to edit existing record
      if (err?.response?.status === 409) {
        const detail = err.response.data?.detail;
        const existingId = detail?.existing_id;
        setDuplicateId(existingId || null);
        setErrors([`An EOD record already exists for ${form.submission_date} at this location.`]);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to submit EOD record');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">EOD Summary — Sales</h1>
        <p className="text-slate-500 text-sm mt-1">Submit daily consult outcomes and sales performance for your location</p>
      </div>
      {errors.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
          {errors.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={14} />{e}
            </div>
          ))}
          {duplicateId && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-red-700">Want to update the existing record instead?</span>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => navigate('/eod/records')}>
                Go to EOD Records →
              </Button>
            </div>
          )}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header fields */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Basic Information</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left bg-white" data-testid="eod-form-date">
                      <CalendarIcon size={14} className="mr-2 text-slate-400" />
                      {form.submission_date ? format(new Date(form.submission_date + 'T00:00:00'), 'MMMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white">
                    <Calendar mode="single"
                      selected={form.submission_date ? new Date(form.submission_date + 'T00:00:00') : undefined}
                      onSelect={(d) => { if (d) { setField('submission_date', format(d, 'yyyy-MM-dd')); setDateOpen(false); } }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                <Select value={form.location_id} onValueChange={v => setField('location_id', v)}>
                  <SelectTrigger className="bg-white" data-testid="eod-form-location">
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Submitted By */}
            {(dropdowns.eod_staff?.length > 0) && (
              <div className="mt-4 space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Submitted By <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Select value={form.submitted_by || '__none__'} onValueChange={v => setField('submitted_by', v === '__none__' ? '' : v)}>
                  <SelectTrigger className="bg-white max-w-xs">
                    <SelectValue placeholder="Select staff member..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__none__">— Not specified —</SelectItem>
                    {(dropdowns.eod_staff || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Consult Metrics — directly after header */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Consult Metrics</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <IntField label="Total Consults Scheduled" name="total_consults_scheduled" value={form.total_consults_scheduled} onChange={setField} required />
              <IntField label="Total Consults Showed" name="total_consults_showed" value={form.total_consults_showed} onChange={setField} required
                helper="Cannot exceed consults scheduled" />
              <IntField label="Full Arch Cases Presented" name="full_arch_cases_presented" value={form.full_arch_cases_presented} onChange={setField} required />
              <IntField label="Non-Full Arch Cases Presented" name="non_full_arch_cases_presented" value={form.non_full_arch_cases_presented} onChange={setField} />
              <div className="space-y-1 lg:col-span-2">
                <Label className="text-slate-700 text-sm font-medium">
                  Qualified Consults
                  <span className="text-slate-400 text-xs font-normal ml-1">(consults who clinically qualify for Full Arch treatment)</span>
                </Label>
                <Input type="number" min="0"
                  value={form.total_qualified_consults === '' || form.total_qualified_consults === null || form.total_qualified_consults === undefined ? '' : form.total_qualified_consults}
                  onChange={e => setField('total_qualified_consults', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                  className="bg-white max-w-xs"
                  data-testid="eod-field-total_qualified_consults"
                  placeholder="Optional" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Today's Consult Outcomes */}
        <Card className="bg-white shadow-sm border-l-4" style={{ borderLeftColor: 'hsl(191, 95%, 43%)' }}>
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Today’s Consult Outcomes</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <IntField label="Closed From Today's Consults" name="closed_today_from_today" value={form.closed_today_from_today} onChange={setField} required />
              <IntField label="Full Arch Sold (Same Day)" name="full_arch_sold_today_from_today" value={form.full_arch_sold_today_from_today} onChange={setField} required
                helper="Cannot exceed FA presented" />
              <IntField label="Arches Sold (Same Day)" name="arches_sold_today_from_today" value={form.arches_sold_today_from_today} onChange={setField} required />
              <IntField label="Total Treatments Sold (Same Day)" name="total_treatments_sold_today_from_today" value={form.total_treatments_sold_today_from_today} onChange={setField} required />
            </div>
          </CardContent>
        </Card>
        {/* Previous Consult Outcomes */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Outcomes From Previous Consults</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <IntField label="Closed From Previous Consults" name="closed_from_previous" value={form.closed_from_previous} onChange={setField} required />
              <IntField label="Full Arch Sold (Previous)" name="full_arch_sold_from_previous" value={form.full_arch_sold_from_previous} onChange={setField} required />
              <IntField label="Arches Sold (Previous)" name="arches_sold_from_previous" value={form.arches_sold_from_previous} onChange={setField} required />
              <IntField label="Non-FA From Previous" name="non_full_arch_from_previous" value={form.non_full_arch_from_previous} onChange={setField} />
              <IntField label="Total Treatments Sold (Previous)" name="total_treatments_sold_from_previous" value={form.total_treatments_sold_from_previous} onChange={setField} required />
            </div>
          </CardContent>
        </Card>
        {/* Financial */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Financial</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">
                  Total Collected
                  <span className="text-slate-400 text-xs font-normal ml-1">(all daily office collections)</span>
                </Label>
                <MoneyInput value={form.total_collections_today} onValueChange={v => setField('total_collections_today', v)}
                  data-testid="eod-field-total_collections_today" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Surgical Production Today</Label>
                <MoneyInput value={form.surgical_production_today} onValueChange={v => setField('surgical_production_today', v)}
                  data-testid="eod-field-surgical_production_today" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Notes */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Notes for the Day</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Mark if office was closed, doctor was out, staff shortages, or any other context for the day's numbers
            </p>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              placeholder="e.g., 'Office closed early due to storm' or 'Dr. Smith out - only 1 doctor available'"
              className="bg-white border-slate-200 min-h-[80px]"
            />
          </CardContent>
        </Card>
        {/* Actions */}
        <div className="flex gap-3 pb-6">
          <Button type="submit" disabled={loading}
            className="gap-2 font-semibold active:scale-[0.98]"
            className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}
            data-testid="eod-form-submit-button"
          >
            <Save size={15} />
            {loading ? 'Submitting...' : 'Submit EOD Record'}
          </Button>
          <Button type="button" variant="outline" className="bg-white" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
